import React, { useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useSiteContent } from "@/lib/siteContent";
import { toast } from "sonner";
import CallbackForm from "@/components/CallbackForm";

export default function Contact() {
  const { t, lang } = useI18n();
  const { settings } = useSiteContent();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", travel_type: "discovery" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source: "contact_page", language: lang });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", message: "", travel_type: "discovery" });
    } catch {
      toast.error(lang === "fr" ? "Une erreur est survenue." : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const emailParis = settings.email_paris || "bonjour@fhe.travel";
  const emailNy = settings.email_ny || "newyork@fhe.travel";
  const phoneParis = settings.phone_paris;
  const phoneNy = settings.phone_ny;

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 pb-32">
      <div className="grid md:grid-cols-2 gap-24 mb-24">
        <div>
          <div className="overline text-[#D4AF37]">{lang === "fr" ? "Contact" : "Contact"}</div>
          <h1 className="font-serif font-light text-5xl md:text-6xl text-[#0A1128] mt-4">{t("contact.title")}</h1>
          <p className="mt-6 text-[#0A1128]/70 max-w-md">{t("contact.lede")}</p>
          <div className="mt-16 space-y-6 text-sm text-[#0A1128]/80">
            <div>
              <span className="overline text-[#D4AF37]">Paris</span>
              <div className="mt-1"><a href={`mailto:${emailParis}`} className="link-gold">{emailParis}</a></div>
              {phoneParis && <div>{phoneParis}</div>}
              {settings.address_paris && <div className="whitespace-pre-line mt-1">{settings.address_paris}</div>}
            </div>
            <div>
              <span className="overline text-[#D4AF37]">New York</span>
              <div className="mt-1"><a href={`mailto:${emailNy}`} className="link-gold">{emailNy}</a></div>
              {phoneNy && <div>{phoneNy}</div>}
              {settings.address_ny && <div className="whitespace-pre-line mt-1">{settings.address_ny}</div>}
            </div>
            {(settings.instagram || settings.pinterest) && (
              <div className="pt-4 flex gap-6 text-xs tracking-[0.24em] uppercase">
                {settings.instagram && <a href={settings.instagram} target="_blank" rel="noreferrer" className="link-gold">Instagram</a>}
                {settings.pinterest && <a href={settings.pinterest} target="_blank" rel="noreferrer" className="link-gold">Pinterest</a>}
              </div>
            )}
          </div>
        </div>
        <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
          <div>
            <label className="overline text-[#0A1128]/60">{lang === "fr" ? "Nom" : "Name"}</label>
            <input required value={form.name} onChange={set("name")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 focus:outline-none focus:border-[#D4AF37]" data-testid="contact-name" />
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="overline text-[#0A1128]/60">Email</label>
              <input required type="email" value={form.email} onChange={set("email")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 focus:outline-none focus:border-[#D4AF37]" data-testid="contact-email" />
            </div>
            <div>
              <label className="overline text-[#0A1128]/60">{lang === "fr" ? "Téléphone" : "Phone"}</label>
              <input value={form.phone} onChange={set("phone")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 focus:outline-none focus:border-[#D4AF37]" data-testid="contact-phone" />
            </div>
          </div>
          <div>
            <label className="overline text-[#0A1128]/60">{lang === "fr" ? "Type" : "Type"}</label>
            <select value={form.travel_type} onChange={set("travel_type")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 focus:outline-none focus:border-[#D4AF37]" data-testid="contact-type">
              {["discovery", "wedding", "champagne", "private", "honeymoon", "proposal", "corporate"].map((k) => (
                <option key={k} value={k}>{t(`booking.types.${k}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="overline text-[#0A1128]/60">Message</label>
            <textarea required rows={5} value={form.message} onChange={set("message")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-3 focus:outline-none focus:border-[#D4AF37] resize-none" data-testid="contact-message" />
          </div>
          <button type="submit" disabled={loading} className="bg-[#0A1128] text-[#F9F8F6] px-10 py-4 text-xs tracking-[0.24em] uppercase hover:bg-[#D4AF37] hover:text-[#0A1128] transition-colors duration-500 disabled:opacity-60" data-testid="contact-submit">
            {loading ? "..." : t("contact.submit")}
          </button>
        </form>
      </div>
      <CallbackForm />
    </div>
  );
}
